import PageHeader from '@/components/page-header';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import ValidationErrors from '@/components/validation-errors';
import { cn } from '@/lib/utils';
import { apps_path, billing_path, new_app_path } from '@/rails/routes';
import type { PlanSummary, ProviderMode, TemplateSummary } from '@/types/platform';
import { Form, Link, router, usePage } from '@inertiajs/react';
import { useState } from 'react';

interface AppsNewProps {
  templates: TemplateSummary[];
  plan: PlanSummary;
  canAddApp: boolean;
  regionChoice: boolean;
  draftEngine: ProviderMode;
  buildCreditsRemaining: number | null;
  form: {
    name: string | null;
    templateKey: string | null;
    description: string | null;
    region: string | null;
  };
  errors: string[];
}

type StartMode = 'template' | 'describe';

const CHOICE_CLASS =
  'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors';
const CHOSEN_CLASS = 'border-foreground bg-muted/40';

const AppsNew = ({
  templates,
  plan,
  canAddApp,
  regionChoice,
  draftEngine,
  buildCreditsRemaining,
  form,
  errors,
}: AppsNewProps) => {
  const { staff } = usePage<{ staff?: boolean }>().props;
  // A refused form comes back where the person left it.
  const [mode, setMode] = useState<StartMode>(
    form.description && !form.templateKey ? 'describe' : 'template'
  );
  const [templateKey, setTemplateKey] = useState(form.templateKey ?? '');
  const [description, setDescription] = useState(form.description ?? '');

  return (
    <div className="space-y-6">
      <PageHeader
        title="New app"
        description="Name it, then pick a starting point. You can change the design later."
      />

      {canAddApp ? null : (
        <Alert>
          <AlertDescription>
            <p>
              Your {plan.name} plan includes {plan.appLimit} {plan.appLimit === 1 ? 'app' : 'apps'}.{' '}
              <Link href={billing_path()} className="text-foreground font-medium underline">
                See plans
              </Link>
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* A refused app is rendered at the address it was sent to, which is not a
          page of its own. Put this form's address back so a refresh lands here. */}
      <Form
        method="post"
        action={apps_path()}
        onError={() =>
          router.replace({ url: new_app_path(), preserveState: true, preserveScroll: true })
        }
        className="space-y-6"
      >
        {({ processing }) => (
          <>
            <ValidationErrors errors={errors} />

            <Card>
              <CardContent className="space-y-2">
                <Label htmlFor="app-name">App name</Label>
                <Input
                  id="app-name"
                  name="app[name]"
                  defaultValue={form.name ?? ''}
                  placeholder="Field tickets"
                  autoComplete="off"
                  required
                  className="sm:max-w-md"
                />

                {regionChoice ? (
                  <div className="space-y-2 pt-3">
                    <Label htmlFor="app-region">Region (optional)</Label>
                    <Input
                      id="app-region"
                      name="app[region]"
                      defaultValue={form.region ?? ''}
                      placeholder="us-east-1"
                      autoCapitalize="none"
                      spellCheck={false}
                      autoComplete="off"
                      aria-describedby="app-region-hint"
                      className="sm:max-w-md"
                    />
                    <p id="app-region-hint" className="text-muted-foreground text-xs">
                      Where this app keeps its records. It is set when the app is made and is not
                      moved later. Leave it blank to use the nearest one.
                    </p>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <h2 id="start-heading">How do you want to start?</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <RadioGroup
                  value={mode}
                  onValueChange={(value) => setMode(value === 'describe' ? 'describe' : 'template')}
                  aria-labelledby="start-heading"
                  className="grid gap-3 sm:grid-cols-2"
                >
                  <label
                    htmlFor="start-template"
                    className={cn(CHOICE_CLASS, mode === 'template' && CHOSEN_CLASS)}
                  >
                    <RadioGroupItem id="start-template" value="template" className="mt-0.5" />
                    <span className="space-y-1">
                      <span className="block text-sm font-medium">Pick a template</span>
                      <span className="text-muted-foreground block text-sm">
                        Lists and fields already made for a kind of work.
                      </span>
                    </span>
                  </label>
                  <label
                    htmlFor="start-describe"
                    className={cn(CHOICE_CLASS, mode === 'describe' && CHOSEN_CLASS)}
                  >
                    <RadioGroupItem id="start-describe" value="describe" className="mt-0.5" />
                    <span className="space-y-1">
                      <span className="block text-sm font-medium">Describe your app</span>
                      <span className="text-muted-foreground block text-sm">
                        Say what you need to keep track of and get a draft to review.
                      </span>
                    </span>
                  </label>
                </RadioGroup>

                {mode === 'template' ? (
                  <div className="space-y-3">
                    <h3 id="template-heading" className="text-sm font-medium">
                      Template
                    </h3>
                    <input type="hidden" name="app[template_key]" value={templateKey} />
                    <RadioGroup
                      value={templateKey}
                      onValueChange={setTemplateKey}
                      aria-labelledby="template-heading"
                      className="grid gap-3 lg:grid-cols-2"
                    >
                      {templates.map((template) => (
                        <TemplateChoice
                          key={template.key}
                          template={template}
                          chosen={templateKey === template.key}
                        />
                      ))}
                    </RadioGroup>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="app-description">What should the app keep track of?</Label>
                    <Textarea
                      id="app-description"
                      name="app[description]"
                      rows={5}
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      placeholder="We schedule crews for site visits, take photos on site, and invoice the customer when the job is done."
                      required
                      aria-describedby="app-description-notes"
                    />
                    <div
                      id="app-description-notes"
                      className="text-muted-foreground space-y-1 text-sm"
                    >
                      <p>You review the draft before it becomes your app's design.</p>
                      {buildCreditsRemaining === null ? null : (
                        <p>A draft uses 1 build credit. {buildCreditsRemaining} left this month.</p>
                      )}
                      {/* Only staff can connect the drafting model, so only staff are told to. */}
                      {draftEngine === 'demo' ? (
                        <p>
                          {staff
                            ? 'Demo mode: the draft is matched to the closest template by keyword. Connect OpenRouter for a model-written draft.'
                            : 'Describing an app is not switched on yet, so the draft will be the closest template. You can also start from a template.'}
                        </p>
                      ) : null}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" disabled={processing || !canAddApp}>
                Create app
              </Button>
              <Button asChild variant="ghost">
                <Link href={apps_path()}>Cancel</Link>
              </Button>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

const TemplateChoice = ({ template, chosen }: { template: TemplateSummary; chosen: boolean }) => {
  const id = `template-${template.key}`;

  return (
    <label htmlFor={id} className={cn(CHOICE_CLASS, 'min-w-0', chosen && CHOSEN_CLASS)}>
      <RadioGroupItem id={id} value={template.key} className="mt-0.5" />
      <span className="min-w-0 space-y-2">
        <span className="block text-sm font-medium">{template.name}</span>
        <span className="text-muted-foreground block text-sm">{template.summary}</span>
        {template.industries.length > 0 ? (
          <span className="text-muted-foreground block text-xs">
            {template.industries.join(' · ')}
          </span>
        ) : null}
        <span className="block space-y-1.5 border-t pt-2">
          {template.lists.map((list) => (
            <span key={list.key} className="block text-sm">
              <span className="font-medium">{list.name}</span>
              <span className="text-muted-foreground"> — {list.fields.join(', ')}</span>
            </span>
          ))}
        </span>
      </span>
    </label>
  );
};

export default AppsNew;
